import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

import {toast} from 'react-toastify'
import axiosInstance from "../../config/axiosInterceptor"

// Video Modal Component for better video viewing
const VideoModal = ({ isOpen, videoUrl, onClose }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        display: isOpen ? "flex" : "none",
        position: "fixed",
        zIndex: 1000,
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px"
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "20px",
          maxWidth: "90%",
          maxHeight: "90%",
          position: "relative",
          boxShadow: "0 10px 30px rgba(0,0,0,0.3)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "rgba(0,0,0,0.7)",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "35px",
            height: "35px",
            fontSize: "18px",
            cursor: "pointer",
            zIndex: 1001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
          title="Close Video"
        >
          ×
        </button>

        <video 
          controls 
          autoPlay
          style={{ 
            width: "100%", 
            height: "auto",
            maxHeight: "70vh",
            borderRadius: "8px"
          }}
        >
          <source src={videoUrl} type="video/mp4" />
          <source src={videoUrl} type="video/webm" />
          <source src={videoUrl} type="video/ogg" />
          Your browser does not support the video tag.
        </video>

        <div style={{ 
          marginTop: "10px", 
          textAlign: "center",
          fontSize: "12px",
          color: "#666"
        }}>
          <i className="fa fa-info-circle" style={{ marginRight: "5px" }}></i>
          Review this video for content moderation
        </div>
      </div>
    </div>
  );
};

const Video = () => {

  const baseUrl = process.env.REACT_APP_BASEURL1;
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(5);
  const [searchUsername, setSearchUsername] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [totalVideos, setTotalVideos] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const pageNumbers = [];
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(totalPages, currentPage + 2);
  // for (let i = startPage; i <= endPage; i++) {
  //   pageNumbers.push(
  //     <li
  //       key={i}
  //       className={`page-item ${currentPage === i ? "active" : ""}`}
  //     >
  //       <button onClick={() => paginate(i)} className="page-link">
  //         {i}
  //       </button>
  //     </li>
  //   );
  // }
  // Handle search functionality
  const handleSearch = (e) => {
    e.preventDefault();
    setSearchUsername(searchTerm.trim());
    setCurrentPage(1); // Reset to first page when searching
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSearchUsername("");
    setCurrentPage(1);
  };

  // Handle video modal
  const openVideoModal = (videoUrl) => {
    setCurrentVideoUrl(videoUrl);
    setIsModalOpen(true);
  };

  const closeVideoModal = () => {
    setIsModalOpen(false);
    setCurrentVideoUrl("");
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Build query parameters
        const params = new URLSearchParams({
          page: currentPage,
          limit: entriesPerPage,
          v: "1" // For vulgar content
        });

        // Add username search if provided
        if (searchUsername) {
          params.append('username', searchUsername);
        }

        const response = await axios.get(
          `${process.env.REACT_APP_BASEURL4}/admin/video?${params.toString()}`
        );

        setVideos(response.data.details);
        setTotalPages(response.data.totalPages);
        setTotalVideos(response.data.totalVideos || response.data.details.length);
        setLoading(false);

        // Show search results info
        if (searchUsername && response.data.details.length === 0) {
          toast.info(`No vulgar videos found for user "${searchUsername}"`);
        }

      } catch (error) {
        console.log("Error fetching data:", error);
        toast.error("Failed to fetch videos");
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, entriesPerPage, searchUsername]);

  // Handle approve/reject actions
  const handleApproveReject = async (videoId, status, actionType) => {
    const confirmMessage = actionType === "approved" 
      ? "Are you sure you want to approve this video? It will be visible to all users."
      : "Are you sure you want to reject this video? It will be hidden from users.";
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_BASEURL4}/admin/content/aprovereject`,
        {
          id: videoId,
          status: status
        }
      );

      if (response.data.success === 1) {
        // Remove the video from the list since it's no longer vulgar (approved/rejected)
        setVideos(videos.filter((video) => video._id !== videoId));
        toast.success(`Video ${actionType} successfully`);
      } else {
        toast.error(response.data.message || `Failed to ${actionType.toLowerCase()} video`);
      }
    } catch (error) {
      console.error(`Error ${actionType.toLowerCase()} video:`, error);
      toast.error(error.response?.data?.message || `Failed to ${actionType.toLowerCase()} video`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm("Are you sure you want to permanently delete this video? This action cannot be undone.")) {
      return;
    }

    try {
      setLoading(true);
      
      // Use the content endpoint for deleting vulgar videos
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_BASEURL4}/admin/content/aprovereject`,
        {
          id: videoId,
          status: "3" // Delete status
        }
      );

      if (response.data.success === 1) {
        setVideos(videos.filter((video) => video._id !== videoId));
        toast.success("Video deleted successfully");
      } else {
        toast.error(response.data.message || "Failed to delete video");
      }
    } catch (error) {
      console.error("Error deleting video:", error);
      toast.error(error.response?.data?.message || "Failed to delete video");
    } finally {
      setLoading(false);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button onClick={() => paginate(i)} className="page-link">
            {i}
          </button>
        </li>
      );
    }

    return pageNumbers;
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <>
      <section className="content-header">
        <h1>Vulgar List</h1>
        <ol className="breadcrumb">
          <li>
            <Link to="/admin">
              <i className="fa fa-dashboard"></i> Home
            </Link>
          </li>
          <li className="active">Video List</li>
        </ol>
      </section>
      <section className="content">
        <div className="row">
          <div className="col-xs-12">
            <div className="box">
              <div className="box-body">
                <div className="row" style={{ padding: "16px 0px" }}>
                  <div className="entries-per-page col-md-6">
                    <label htmlFor="entries">Entries per page:</label>
                    <select
                      id="entries"
                      value={entriesPerPage}
                      onChange={(e) => setEntriesPerPage(parseInt(e.target.value))}
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </select>
                  </div>
                  <div className="search-section col-md-6">
                    <form onSubmit={handleSearch} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div style={{ position: "relative", flex: 1 }}>
                        <input
                          type="text"
                          placeholder="Search by username..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            paddingRight: searchTerm ? "35px" : "12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            fontSize: "14px"
                          }}
                        />
                        {searchTerm && (
                          <button
                            type="button"
                            onClick={clearSearch}
                            style={{
                              position: "absolute",
                              right: "8px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              fontSize: "16px",
                              cursor: "pointer",
                              color: "#999"
                            }}
                            title="Clear search"
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        style={{
                          padding: "8px 16px",
                          backgroundColor: "#007cba",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: loading ? "not-allowed" : "pointer",
                          fontSize: "14px"
                        }}
                      >
                        <i className="fa fa-search" style={{ marginRight: "5px" }}></i>
                        Search
                      </button>
                    </form>
                    {searchUsername && (
                      <div style={{ 
                        marginTop: "8px", 
                        fontSize: "12px", 
                        color: "#666",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                      }}>
                        <span>
                          <i className="fa fa-filter" style={{ marginRight: "5px" }}></i>
                          Showing vulgar videos for: <strong>"{searchUsername}"</strong>
                        </span>
                        <span style={{ color: "#007cba" }}>
                          {totalVideos} result{totalVideos !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <table className="table table-bordered table-striped">
                  <thead>
                  <tr>
                      <th>Sr.</th>
                      <th>User Info</th>
                      <th>Video Preview</th>
                      <th>Safe %</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  {loading ? (
                    <tbody>
                      <tr>
                        <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                            <i className="fa fa-spinner fa-spin"></i>
                            <span>Loading vulgar videos...</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  ) : videos.length === 0 ? (
                    <tbody>
                      <tr>
                        <td colSpan="6" style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                          <div>
                            <i className="fa fa-exclamation-circle" style={{ fontSize: "24px", marginBottom: "10px", display: "block" }}></i>
                            {searchUsername 
                              ? `No vulgar videos found for username "${searchUsername}"`
                              : "No vulgar videos found"
                            }
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <tbody>
                    {videos.map((item, index) => {
                      const i = index + 1 + (currentPage - 1) * entriesPerPage;
                      const userInfo = item.userInfo || {};
                      return (
                        <tr key={item._id || index}>
                            <td>{i}</td>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <div style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "50%",
                                  backgroundColor: "#f0f0f0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  overflow: "hidden"
                                }}>
                                  {userInfo.picture ? (
                                    <img 
                                      src={userInfo.picture} 
                                      alt="User" 
                                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                  ) : (
                                    <i className="fa fa-user" style={{ color: "#999" }}></i>
                                  )}
                                </div>
                                <div>
                                  <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                                    {userInfo.username || item.username || "Unknown"}
                                  </div>
                                  <div style={{ fontSize: "12px", color: "#666" }}>
                                    {userInfo.name || "No name"}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td style={{ position: "relative" }}>
                              <div style={{ position: "relative", display: "inline-block" }}>
                                <video
                                  src={item?.video}
                                  loop={true}
                                  muted={true}
                                  style={{
                                    height: "120px",
                                    width: "120px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    border: "2px solid #ddd"
                                  }}
                                  onClick={() => openVideoModal(item?.video)}
                                  onMouseEnter={(e) => {
                                    const video = e.target;
                                    if (video.paused) {
                                      const playPromise = video.play();
                                      if (playPromise !== undefined) {
                                        playPromise.catch(() => {
                                          // Auto-play was prevented or failed
                                          // This is normal, just ignore the error
                                        });
                                      }
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    const video = e.target;
                                    if (!video.paused) {
                                      video.pause();
                                      video.currentTime = 0; // Reset to beginning
                                    }
                                  }}
                                  title="Click to view full video"
                                ></video>
                                <div style={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  backgroundColor: "rgba(0,0,0,0.7)",
                                  color: "white",
                                  borderRadius: "50%",
                                  width: "35px",
                                  height: "35px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  fontSize: "14px"
                                }} onClick={() => openVideoModal(item?.video)}>
                                  <i className="fa fa-play"></i>
                                </div>
                              </div>
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                                {item?.lastSafeValue && item.lastSafeValue.length > 0 ? (
                                  <>
                                    <span style={{
                                      fontWeight: "bold",
                                      color: item.lastSafeValue * 100 < 50 ? "#d9534f" : 
                                             item.lastSafeValue * 100 < 80 ? "#f0ad4e" : "#5cb85c",
                                      fontSize: "16px"
                                    }}>
                                      {Math.round(item.lastSafeValue * 100)}%
                                    </span>
                                    <span style={{ fontSize: "10px", color: "#666" }}>
                                      Safety Score
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span style={{ color: "#999", fontSize: "14px" }}>—</span>
                                    <span style={{ fontSize: "10px", color: "#999" }}>Not scored</span>
                                  </>
                                )}
                              </div>
                            </td>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span className="label label-warning" style={{ 
                                  backgroundColor: "#ff9800", 
                                  padding: "4px 8px", 
                                  borderRadius: "4px",
                                  fontSize: "11px"
                                }}>
                                  <i className="fa fa-exclamation-triangle" style={{ marginRight: "4px" }}></i>
                                  Pending Review
                                </span>
                                <small style={{ color: "#666", fontSize: "10px" }}>
                                  Flagged for content
                                </small>
                              </div>
                            </td>
                            <td>
                              <ul
                                className="admin-action btn btn-default"
                                style={{
                                  backgroundColor: "#f4f4f4",
                                  color: "#fff !important",
                                }}
                              >
                                <li className="dropdown">
                                  <a
                                    className="dropdown-toggle"
                                    style={{ color: "black" }}
                                    data-toggle="dropdown"
                                    href="#"
                                    aria-expanded="false"
                                  >
                                    Action  
                                  </a>
                                  <ul className="dropdown-menu dropdown-menu-right">
                                    <>
                                      <li role="presentation">
                                        <Link
                                          role="menuitem"
                                          tabIndex={-1}
                                          to=""
                                          onClick={() => handleApproveReject(item._id, "0", "approved")}
                                        >
                                          <i className="fa fa-check" style={{ marginRight: "5px", color: "green" }}></i>
                                          Approve
                                        </Link>
                                      </li>
                                      <li role="presentation">
                                        <Link
                                          role="menuitem"
                                          tabIndex={-1}
                                          to=""
                                          onClick={() => handleApproveReject(item._id, "2", "rejected")}
                                        >
                                          <i className="fa fa-times" style={{ marginRight: "5px", color: "orange" }}></i>
                                          Reject
                                        </Link>
                                      </li>
                                      <li role="presentation">
                                        <Link
                                          role="menuitem"
                                          tabIndex={-1}
                                          to=""
                                          onClick={() => handleDelete(item._id)}
                                        >
                                          <i className="fa fa-trash" style={{ marginRight: "5px", color: "red" }}></i>
                                          Delete
                                        </Link>
                                      </li>
                                    </>
                                  </ul>
                                </li>
                              </ul>
                            </td>
                          </tr>
                      );
                    })}
                  </tbody>
                  )}
                </table>
                <div className="row">
                  <div className="col-md-12">
                    <ul className="pagination">
                      <li
                        className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                      >
                        <button
                          onClick={() => paginate(currentPage - 1)}
                          className="page-link"
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {renderPageNumbers()}
                      <li className="page-item disabled">
        <span className="page-link">Page {currentPage} of {totalPages}</span>
      </li>
      {pageNumbers}
                      <li
                        className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                      >
                        <button
                          onClick={() => paginate(currentPage + 1)}
                          className="page-link"
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal for full-screen viewing */}
      <VideoModal 
        isOpen={isModalOpen}
        videoUrl={currentVideoUrl}
        onClose={closeVideoModal}
      />
    </>
  );
};

export default Video;
